import { FC, JSX, ReactNode } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Typography,
} from '@mui/joy';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';
import WorkspacePremiumRoundedIcon from '@mui/icons-material/WorkspacePremiumRounded';
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';
import { ENTERPRISE_COMPANY_TYPE, formatDate } from '@consolidocs-ui/utils';
import { CompanyType } from '@consolidocs-ui/types';
import { isProvisioningFailed } from '@consolidocs-ui/hooks/useEnterpriseOrgStatus';
import { ProvisionList } from 'Components/CreateOrEditCompany/Step2InfrastructureConfig';

interface CompanyCardProps {
  company: CompanyType;
  onEdit: (id?: string) => void;
  onDelete: (id?: string) => void;
  /** Starts a fresh infrastructure build for a failed enterprise org. */
  onRetryProvisioning?: (id?: string) => void;
  /** True while this card's retry request is in flight. */
  isRetrying?: boolean;
}

/** Shared type check so the list and the card agree on what "enterprise" is. */
export const isEnterpriseCompany = (company: CompanyType): boolean =>
  (company.companyType || '').toLowerCase() === ENTERPRISE_COMPANY_TYPE;

// Visual identity for each organization type, keyed off the same enum the
// backend uses. Adding a new type only needs a new entry here.
const TYPE_META = {
  enterprise: {
    label: 'Enterprise',
    color: 'primary' as const,
    Icon: WorkspacePremiumRoundedIcon,
  },
  general: {
    label: 'General',
    color: 'neutral' as const,
    Icon: BusinessRoundedIcon,
  },
};

const getTypeMeta = (company: CompanyType) =>
  (company.companyType || '').toLowerCase() === ENTERPRISE_COMPANY_TYPE
    ? { key: 'enterprise' as const, ...TYPE_META.enterprise }
    : { key: 'general' as const, ...TYPE_META.general };

const EmptyValue: FC = () => (
  <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
    —
  </Typography>
);

/** A labelled row of chips (Industry, Contract types, …). */
const MetaRow: FC<{ label: string; children: ReactNode }> = ({
  label,
  children,
}) => (
  <Box className="company-card__meta">
    <Typography level="body-xs" className="company-card__meta-label">
      {label}
    </Typography>
    <Box className="company-card__chips">{children}</Box>
  </Box>
);

export const CompanyCard: FC<CompanyCardProps> = ({
  company,
  onEdit,
  onDelete,
  onRetryProvisioning,
  isRetrying = false,
}): JSX.Element => {
  const type = getTypeMeta(company);
  const isFailed =
    type.key === 'enterprise' &&
    isProvisioningFailed(company.provisioning_status);
  const contractTypes = company.contract_types_display ?? [];

  const flags = {
    isDbCreated: !!company.isDbCreated,
    isCognitoPoolCreated: !!company.isCognitoPoolCreated,
    isSqsReady: !!company.isSqsReady,
    isServiceReady: !!company.isServiceReady,
  };

  return (
    <Card variant="plain" className={`company-card company-card--${type.key}`}>
      <span className="company-card__accent" aria-hidden />

      <CardContent>
        <Box className="company-card__head">
          <Box className="company-card__identity">
            <Typography level="title-md" className="company-card__name">
              {company.name}
            </Typography>
            <Typography level="body-xs" className="company-card__date">
              Created{' '}
              {company.created_at ? formatDate(company.created_at) : '—'}
            </Typography>
            {/* The organization email is the company admin's login. */}
            <Typography
              level="body-xs"
              className="company-card__admin"
              startDecorator={<MailOutlineRoundedIcon sx={{ fontSize: '0.9rem' }} />}
              title={company.email || undefined}
            >
              Admin: {company.email || '—'}
            </Typography>
          </Box>

          <Chip
            size="sm"
            variant={type.key === 'enterprise' ? 'solid' : 'soft'}
            color={type.color}
            startDecorator={<type.Icon sx={{ fontSize: '1rem' }} />}
            className="company-card__type"
          >
            {type.label}
          </Chip>
        </Box>

        <Typography level="body-sm" className="company-card__desc">
          {company.description || 'No description provided.'}
        </Typography>

        <Divider className="company-card__divider" />
        {type.key === 'enterprise' && (
          <>
            <ProvisionList flags={flags} isPolling={false} />
            {isFailed && (
              <Typography level="body-xs" color="danger">
                Infrastructure setup failed ({company.provisioning_status}).
              </Typography>
            )}
            <Divider className="company-card__divider" />
          </>
        )}

        <MetaRow label="Industry">
          {company.industry_display?.name ? (
            <Chip size="sm" variant="outlined" color="neutral">
              {company.industry_display.name}
            </Chip>
          ) : (
            <EmptyValue />
          )}
        </MetaRow>

        <MetaRow label="Contract types">
          {contractTypes.length ? (
            contractTypes.map((contract) => (
              <Chip
                key={contract.id}
                size="sm"
                variant="outlined"
                color="neutral"
              >
                {contract.name}
              </Chip>
            ))
          ) : (
            <EmptyValue />
          )}
        </MetaRow>

        <Box className="company-card__footer">
          {isFailed && onRetryProvisioning && (
            <Button
              size="sm"
              variant="soft"
              color="warning"
              startDecorator={<ReplayRoundedIcon />}
              loading={isRetrying}
              onClick={() => onRetryProvisioning(company.id)}
            >
              Retry setup
            </Button>
          )}
          <Button
            size="sm"
            variant="soft"
            color="neutral"
            startDecorator={<EditRoundedIcon />}
            onClick={() => onEdit(company.id)}
          >
            Edit
          </Button>
          <Button
            size="sm"
            variant="soft"
            color="danger"
            startDecorator={<DeleteRoundedIcon />}
            onClick={() => onDelete(company.id)}
          >
            Delete
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default CompanyCard;
